import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import moment from 'moment'
import type { Moment } from 'moment'
import { duration } from 'moment'

import API from 'app/services/tasks/taskApi'
import { TaskModel } from 'app/models/tasks/taskModel'
import { TaskType } from 'app/constants/taskType'
import { TaskStatus } from 'app/constants/taskStatus'
import { getTaskType } from 'app/services/task/taskTypeResolver'
import { getTaskStatus } from 'app/services/task/taskStatusResolver'
import { getTaskTypeName } from 'app/common/converters/task/taskTypeToTextConverter'
import { getTaskStatusName } from 'app/common/converters/task/taskStatusToTextConverter'
import { formatSecondsDuration } from 'app/common/dateHelper'
import { PRECISE_TIME_FORMAT, SERVICE_TIME_FORMAT } from 'app/constants/date'
import {
  isEmptyDocumentCode,
  translateErrorCode,
} from 'app/services/printout/emptyDocumentReason'
import { TableProvider } from '@/modules/drivers/context/TableContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { AdminDateField, CheckboxField } from '@/modules/shared/components'
import {
  AdminColumn,
  AdminTable,
  AdminToolbar,
  Pill,
} from '../shared/AdminTable'

const TYPE_COLORS: Record<string, string> = {
  Import: '#1F97FC',
  Timesheet: '#A66887',
  Printout: '#F96E21',
  Violation: '#595DA6',
  SaveImport: '#21807F',
  ImiExport: '#F86386',
  BusinessTrip: '#4A90A4',
}

const STATUS_COLORS: Record<string, string> = {
  New: '#808080',
  Pending: '#0094FF',
  Importing: '#FFBF51',
  Completed: '#557C3A',
  Error: '#FF3A47',
}

const REFRESH_MS = 5000

/**
 * Zakładka PRZETWARZANIE panelu admina - funkcyjna, na react-query.
 *
 * Zastępuje `app/components/administration/tasks.tsx`. Lista odświeża się co 5 s tak jak
 * dotąd, tyle że przez `refetchInterval` zamiast własnego `setInterval`.
 */
const AdminTasksPageContent: React.FC = () => {
  const { t } = useLocalize()
  const api = React.useMemo(() => API.create(), [])

  const [dateFrom, setDateFrom] = React.useState<Moment>(
    moment().startOf('day'),
  )
  const [dateTo, setDateTo] = React.useState<Moment | null>(null)
  const [dateToEnabled, setDateToEnabled] = React.useState(false)
  const [search, setSearch] = React.useState('')

  const query = useQuery({
    queryKey: [
      'admin',
      'tasks',
      dateFrom.format(SERVICE_TIME_FORMAT),
      dateToEnabled && dateTo ? dateTo.format(SERVICE_TIME_FORMAT) : '',
    ],
    queryFn: async () => {
      const response = await api.getTasks(
        dateFrom.format(SERVICE_TIME_FORMAT),
        dateToEnabled && dateTo ? dateTo.format(SERVICE_TIME_FORMAT) : '',
      )
      return response.content.data as TaskModel[]
    },
    refetchInterval: REFRESH_MS,
  })

  const tasks = React.useMemo(() => {
    const all = query.data ?? []
    const needle = search.trim().toLowerCase()
    if (!needle) return all
    return all.filter((task) =>
      task.companyName?.toLowerCase().includes(needle),
    )
  }, [query.data, search])

  const companiesCount = React.useMemo(
    () => new Set(tasks.map((task) => task.companyName)).size,
    [tasks],
  )

  const formatDate = (value?: Moment) =>
    value ? moment(value).format(PRECISE_TIME_FORMAT) : ''

  const columns: Array<AdminColumn<TaskModel>> = [
    { key: 'companyName', header: t('tasks.companyName'), width: 270 },
    {
      key: 'type',
      header: t('tasks.taskType'),
      width: 200,
      render: (task) => (
        <Pill color={TYPE_COLORS[TaskType[getTaskType(task.type)]]}>
          {getTaskTypeName(getTaskType(task.type), t)}
        </Pill>
      ),
    },
    {
      key: 'status',
      header: t('tasks.taskStatus'),
      width: 150,
      render: (task) => (
        <Pill color={STATUS_COLORS[TaskStatus[getTaskStatus(task.status)]]}>
          {getTaskStatusName(getTaskStatus(task.status), t)}
        </Pill>
      ),
    },
    {
      key: 'created',
      header: t('tasks.created'),
      width: 160,
      render: (task) => formatDate(task.created),
    },
    {
      key: 'locked',
      header: t('tasks.locked'),
      width: 160,
      render: (task) => formatDate(task.locked),
    },
    {
      key: 'completed',
      header: t('tasks.completed'),
      width: 160,
      render: (task) => formatDate(task.completed),
    },
    {
      key: 'duration',
      header: t('tasks.duration'),
      width: 105,
      render: (task) => {
        if (!task.locked || !task.completed) return '?'
        const diff = duration(moment(task.completed).diff(moment(task.locked)))
        const seconds = diff.asSeconds()
        return (
          <span
            style={{
              fontWeight: seconds > 60 ? 500 : 400,
              color: seconds > 180 ? '#f00' : undefined,
            }}
          >
            {formatSecondsDuration(diff)}
          </span>
        )
      },
    },
    {
      key: 'externalImporter',
      header: t('tasks.remote'),
      width: 90,
      render: (task) =>
        task.externalImporter ? (
          <Pill color="#B200FF">{t('answer.yes')}</Pill>
        ) : (
          t('answer.no')
        ),
    },
    {
      key: 'message',
      header: t('tasks.message'),
      render: (task) => {
        const message = isEmptyDocumentCode(task.message)
          ? translateErrorCode(t, task.message)
          : task.message
        return <span title={message}>{message}</span>
      },
    },
  ]

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-content-primary">
      <AdminToolbar search={search} onSearch={setSearch}>
        <div style={{ width: 180 }}>
          <AdminDateField
            withTime
            value={dateFrom}
            onChange={(date) => date && setDateFrom(moment(date))}
          />
        </div>
        <span>-</span>
        <CheckboxField
          id="tasks-date-to"
          checked={dateToEnabled}
          onChange={(event) => {
            setDateToEnabled(event.target.checked)
            if (event.target.checked && !dateTo) setDateTo(moment())
          }}
        />
        <div style={{ width: 180, opacity: dateToEnabled ? 1 : 0.3 }}>
          <AdminDateField
            withTime
            disabled={!dateToEnabled}
            value={dateTo}
            onChange={(date) => date && setDateTo(moment(date))}
          />
        </div>
        {companiesCount > 0 && (
          <span className="tw-text-content-muted tw-ml-auto tw-text-sm">
            {t('tasks.companyName')}: <b>{companiesCount}</b> · {tasks.length}
          </span>
        )}
      </AdminToolbar>

      <AdminTable
        idPrefix="task"
        rows={tasks}
        columns={columns}
        rowId={(task) => task.id}
        selectable={false}
        isLoading={query.isLoading}
      />
    </div>
  )
}

/** `AdminTable` czyta zaznaczenie z `TableContext`, więc potrzebuje providera nawet tam,
 *  gdzie kolumny zaznaczania nie ma. */
export const AdminTasksPage: React.FC = () => (
  <TableProvider>
    <AdminTasksPageContent />
  </TableProvider>
)
